import {BUILDING_CONSENT_STATUS_ENUM, BuildingConsentStatusEnumId} from '@classictechsolutions/hubapi-transpiled-enums';
import {ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, Output} from '@angular/core';
import { take } from 'rxjs';
import {CbDialogService} from '@app/shared/components/dialog/cb-dialog.service';
import {ConsentCouncilReceievedDialogComponent} from '../../consent-council-received-dialog/consent-council-received-dialog.component';
import {EditBuildingConsentDialogComponent} from '../edit-dialog/edit-dialog.component';
import {EditConsentDetailsDialogComponent} from './../../edit-consent-details-dialog/edit-consent-details-dialog.component';
import {GrantConsentDialogComponent} from '../../grant-consent-dialog/grant-consent-dialog.component';
import {IBuildingConsentMappedItem} from '@app/logic/lot-consents';
import {LotConsentViewService} from '../../service/lot-consent-view.service';
import {LotDesignPermissions} from '@app/core/permissions';
import {ReassignBuildingConsentDialogComponent} from '../reassign-dialog/reassign-dialog.component';
import {SubmittedToCouncilDilaogComponent} from '../../submitted-to-council-dilaog/submitted-to-council-dilaog.component';

@Component({
    selector: 'cb-lot-building-consent-action-bar',
    templateUrl: './action-bar.component.html',
    styleUrls: ['./action-bar.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class BuildingConsentActionBarComponent {
    @Input() public readonly buildingConsent: IBuildingConsentMappedItem;
    @Input() public readonly hasBuildingConsentDocument: boolean;

    /** replace this with "this.buildingConsentViewService.backToList()" like council rfis */
    @Output() public readonly backToList = new EventEmitter<void>();
    @Output() public reloadLotMappedItem = new EventEmitter();

    constructor(
        public readonly cbDialog: CbDialogService,
        public readonly lotDesignPermissions: LotDesignPermissions,
        public readonly lotConsentViewService: LotConsentViewService,
        private readonly cdRef: ChangeDetectorRef,
    ) { }

    public editBuildingConsent(): void {
        this.buildingConsent.$reload()
            .pipe(take(1)).subscribe(() => {
                this.cbDialog.open(EditBuildingConsentDialogComponent, {
                    data: {
                        buildingConsent: this.buildingConsent.$clone(),
                        hasPrimaryBuildingConsent: this.lotConsentViewService.hasPrimaryBuildingConsent
                    },
                    minWidth: '33%',
                });
                this.cdRef.detectChanges();
            });
    }

    public canDeleteConsent(): boolean {
        return this.lotDesignPermissions.canDeleteConsent();
    }

    public canEditConsent(): boolean {
        return this.lotDesignPermissions.canEditConsent();
    }

    public canEditConsentDetails(): boolean {
        return this.lotDesignPermissions.canEditConsentDetails();
    }

    public canChangeConsentStatus(): boolean {
        return this.lotDesignPermissions.canEditConsent();
    }

    public setInQueue(): void {
        this.setStatusAndSave(BUILDING_CONSENT_STATUS_ENUM.InQueue);
    }

    public setOnHold(): void {
        this.setStatusAndSave(BUILDING_CONSENT_STATUS_ENUM.OnHold);
    }

    public setInProgress(): void {
        this.setStatusAndSave(BUILDING_CONSENT_STATUS_ENUM.InProgress);
    }

    public setSubmittedToCouncil(): void {
        this.cbDialog.open(SubmittedToCouncilDilaogComponent, {
            data: {
                mappedItem: this.buildingConsent.$clone(),
            },
            minWidth: '33%',
        });
    }

    public editConsentDetails(): void {
        this.cbDialog.open(EditConsentDetailsDialogComponent, {
            data: {
                mappedItem: this.buildingConsent.$clone(),
            },
            minWidth: '33%',
        });
    }

    public setCouncilReceived(): void {
        this.cbDialog.open(ConsentCouncilReceievedDialogComponent, {
            data: {
                mappedItem: this.buildingConsent.$clone(),
            },
            minWidth: '33%',
        });
    }

    public grantConsent(): void {
        if (!this.hasBuildingConsentDocument) {
            return;
        }
        this.cbDialog.open(GrantConsentDialogComponent, {
            data: {
                mappedItem: this.buildingConsent.$clone(),
            },
            minWidth: '33%',
        }).afterClosed().pipe(take(1)).subscribe(() => {
            this.reloadLotMappedItem.emit();
            this.cdRef.detectChanges();
        });
    }

    public denyConsent(): void {
        this.cbDialog.confirm({
            dialogHeading: 'Consent Denied',
            message: 'Are you sure you want to mark this Resource Consent as denied?',
            confirmed: () => {
                this.setStatusAndSave(BUILDING_CONSENT_STATUS_ENUM.Declined);
            }
        });
    }

    public assignBuildingConsent(): void {
        this.buildingConsent.$reload()
            .pipe(take(1)).subscribe(() => {
                this.cdRef.detectChanges();
                this.cbDialog.open(ReassignBuildingConsentDialogComponent, {
                    data: {
                        buildingConsent: this.buildingConsent.$clone(),
                    },
                    minWidth: '33%',
                });
            });
    }

    public goBackToList(): void {
        // this.buildingConsentViewService.backToList();
        /** replace this with "this.buildingConsentViewService.backToList()" like council rfis */
        this.backToList.emit();
        this.lotConsentViewService?.backToList();
    }

    public deleteBuildingConsent(): void {
        this.cbDialog.confirm({
            dialogHeading: 'Delete Building Consent from this Lot',
            message: 'Are you sure you want to delete this Building Consent?',
            confirmed: () => {
                this.buildingConsent.delete().pipe(take(1)).subscribe((result) => {
                    this.cdRef.detectChanges();
                    if (result) {
                        this.goBackToList();
                    }
                });
            }
        });
    }

    private setStatusAndSave(status: BuildingConsentStatusEnumId): void {
        this.buildingConsent.consentStatus = status;
        this.buildingConsent.$save().pipe(take(1)).subscribe(() => {
            this.cdRef.detectChanges();
        });
    }
}
